import { useState, useEffect } from "react";
import { callAPI } from "../hooks/CallAPI";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingWrapper from '../components/LoadingWrapper'; 
import Input from "../components/FormElements/Input"; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Footer from "../components/Footer.jsx";
import { CircleCheckBig } from 'lucide-react';


const ResetPassword = () => {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [searchParams] = useSearchParams();
    const token_reset_password = searchParams.get("token");
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isValidToken, setIsValidToken] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    
    //  Fonction de validation des mots de passe
    const validatePassword = (password) => {
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;
        return regex.test(password);
    };

    //  Vérification de la validité du token
    useEffect(() => {
        const validateToken = async () => {
            setIsLoading(true);
            try {
                const response = await callAPI('/validate-token', { token_reset_password });
                if (response.success) {
                    setIsValidToken(true);
                } else {
                    toast.error("Le lien pour créer votre mot de passe est expiré.");
                    navigate("/");
                }
            } catch (error) {
                toast.error("Le lien pour créer votre mot de passe est expiré.");
                navigate("/");
            } finally {
                setIsLoading(false);
            }
        };

        validateToken();
    }, [token_reset_password, navigate]);

    // Gestion du formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error("Les mots de passe ne correspondent pas.");
            return;
        }

        if (!validatePassword(password)) {
            toast.error("Le mot de passe ne respecte pas les critères de sécurité.");
            setIsLoading(false);
            return;
        }

        try {
            const response = await callAPI('/reset-password', {
                token_reset_password,
                password
            });

            if (response.success) {
                toast.success("Mot de passe réinitialisé avec succès !");
                navigate("/");
            }
        } catch (error) {
            toast.error("Erreur lors de la réinitialisation de votre mot de passe.");
        }
    };


    return (
        <div className="flex flex-col justify-between h-screen">
            <div className="header bg-mgray text-mdark py-8 px-10 text-center">
                <h1 className="text-xl sm:text-4xl font-extrabold text-mlime mb-4 uppercase">Réinitialiser votre mot de passe</h1>
                <p className="text-lg font-bold text-custom-color">Bienvenue dans votre espace client</p>
            </div>

            <div className="container mx-auto pt-10 text-center">
                <p className="text-sm">Pour accéder à votre espace client sécurisé, vous devez définir un mot de passe conforme aux recommandations suivantes :</p>
                <ul className="mt-4 ml-8 text-sm text-center">
                    <li><CircleCheckBig className="inline pr-1 w-5 text-mlime"/> Au moins 8 caractères</li>
                    <li><CircleCheckBig className="inline pr-1 w-5 text-mlime"/> Inclure une majuscule et une minuscule</li>
                    <li><CircleCheckBig className="inline pr-1 w-5 text-mlime"/> Contenir au moins un chiffre</li>
                    <li><CircleCheckBig className="inline pr-1 w-5 text-mlime"/> Inclure au moins un caractère spécial (*@$!%*?&*)</li>
                </ul>
            </div>

            <div className="container mx-auto px-10 py-10">
                <form id="resetPasswordForm" className="m-10 w-full max-w-md mx-auto" onSubmit={handleSubmit}>
                    
                    <Input
                        name="password"
                        label="Définir un mot de passe"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type={showPassword ? "text" : "password"} 
                    />

                    <Input
                        name="confirmPassword"
                        label="Confirmer le mot de passe"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        type={showPassword ? "text" : "password"} 
                    />

                    <div className="mt-4 flex items-center">
                        <input
                            type="checkbox"
                            id="showPasswordCheckbox"
                            checked={showPassword}
                            onChange={() => setShowPassword(!showPassword)}
                            className="mr-2"
                        />
                        <label htmlFor="showPasswordCheckbox" className="text-sm">Afficher les mots de passe</label>
                    </div>

                    <div className="relative mt-6">
                        <button 
                            type="submit" 
                            className="bg-mlime px-6 py-4 rounded-3xl w-full max-w-lg flex items-center justify-center text-white font-bold text-center m-auto duration-500 hover:bg-colorbutton"
                        >
                            <LoadingWrapper isLoading={isLoading}>
                                Réinitialiser le mot de passe <FontAwesomeIcon className="ml-3" icon="fa-regular fa-paper-plane" />
                            </LoadingWrapper>
                        </button>
                    </div>
                </form>
            </div>
            <Footer />
        </div>
    );
};

export default ResetPassword;
